/**
 * Email Queue row and its status helpers. A row opens the email it stands for.
 */
import type { ReactNode } from 'react';
import type { EmailQueueEntry } from '../../lib/api/emailQueue';
import { Button } from '../../components/ui/button';
import { TableCell, TableRow } from '../../components/ui/table';
import {
  Ban,
  CheckCircle,
  Clock,
  Eye,
  Loader2,
  MailCheck,
  MailX,
  RotateCw,
  Send,
  SkipForward,
  XCircle,
  type LucideIcon,
} from 'lucide-react';

const STATUS_CONFIG: Record<string, { color: string; bg: string; icon: LucideIcon }> = {
  pending: { color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', icon: Clock },
  sending: { color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200', icon: Send },
  sent: { color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', icon: CheckCircle },
  delivered: { color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', icon: MailCheck },
  bounced: { color: 'text-red-700', bg: 'bg-red-50 border-red-200', icon: MailX },
  failed: { color: 'text-red-700', bg: 'bg-red-50 border-red-200', icon: XCircle },
  cancelled: { color: 'text-gray-600', bg: 'bg-gray-50 border-gray-200', icon: Ban },
  skipped: { color: 'text-purple-700', bg: 'bg-purple-50 border-purple-200', icon: SkipForward },
};

const ACRONYMS = /\b(po|ncr|grn|rfq|asn|otif)\b/g;

/**
 * Sentence case for a status or type key.
 *
 * Derived, not looked up, so a type first sent tomorrow reads correctly today.
 *
 * @param key - snake_case key, e.g. `po_status_changed`.
 * @returns the readable label, e.g. `PO status changed`.
 */
export function humanise(key: string): string {
  const words = key.replace(/_/g, ' ').replace(ACRONYMS, (word) => word.toUpperCase());
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The one status to show: what the provider reported about a sent email
 * supersedes the queue's own "sent".
 *
 * @param entry - the queue entry.
 * @returns the status key to display.
 */
export function effectiveStatus(entry: EmailQueueEntry): string {
  if (entry.bounced_at) return 'bounced';
  if (entry.delivered_at) return 'delivered';
  return entry.status;
}

interface StatusBadgeProps {
  status: string;
  /** Draws an eye: the recipient opened it. */
  opened?: boolean;
  title?: string;
}

export function StatusBadge({ status, opened = false, title }: StatusBadgeProps) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG['pending'];
  const Icon = cfg.icon;
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${cfg.bg} ${cfg.color}`}
    >
      <Icon className="h-3 w-3" />
      {humanise(status)}
      {opened && <Eye className="h-3 w-3" aria-label="Opened" />}
    </span>
  );
}

/**
 * How long ago something happened.
 *
 * @param dateStr - an ISO timestamp, or null.
 * @returns e.g. `5m ago`, or `-` when there is no timestamp.
 */
export function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '-';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

interface QueueRowProps {
  entry: EmailQueueEntry;
  /** The queue holds more than one customer's mail, so a row names its own. */
  showCustomer: boolean;
  cancellingId: number | null;
  retryingId: number | null;
  onCancel: (id: number) => void;
  onRetry: (id: number) => void;
  onView: (id: number) => void;
}

export function QueueRow({
  entry, showCustomer, cancellingId, retryingId, onCancel, onRetry, onView,
}: QueueRowProps) {
  let outcome: ReactNode = '-';
  if (entry.sent_at) outcome = timeAgo(entry.sent_at);
  else if (entry.cancelled_at) outcome = timeAgo(entry.cancelled_at);
  else if (entry.error_message) {
    outcome = (
      <span className="inline-block max-w-[160px] truncate align-bottom text-xs text-red-600" title={entry.error_message}>
        {entry.error_message}
      </span>
    );
  }

  return (
    <TableRow
      className="cursor-pointer hover:bg-muted/50"
      onClick={() => onView(entry.id)}
    >
      <TableCell className="max-w-[220px]">
        <p className="truncate text-sm font-medium" title={entry.recipient_email}>{entry.recipient_email}</p>
        {(entry.recipient_company_name || entry.recipient_side) && (
          <p className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
            {entry.recipient_side && (
              <span className="shrink-0 rounded bg-muted px-1 font-medium">
                {humanise(entry.recipient_side)}
              </span>
            )}
            <span className="truncate" title={entry.recipient_company_name}>
              {entry.recipient_company_name}
            </span>
          </p>
        )}
      </TableCell>
      <TableCell className="max-w-[320px] text-sm">
        {/* The row is the mouse target; this is the same action for a keyboard. */}
        <button
          type="button"
          className="block max-w-full truncate text-left hover:underline"
          data-entry-id={entry.id}
          title={entry.subject}
          aria-label={`View email to ${entry.recipient_email}: ${entry.subject}`}
          onClick={(event) => {
            event.stopPropagation();
            onView(entry.id);
          }}
        >
          {entry.subject}
        </button>
        {showCustomer && entry.customer_name && (
          <p className="truncate text-xs text-muted-foreground" title={entry.customer_name}>
            From {entry.customer_name}
          </p>
        )}
      </TableCell>
      <TableCell>
        <StatusBadge
          status={effectiveStatus(entry)}
          opened={Boolean(entry.delivered_at && entry.opened_at)}
          title={entry.bounced_at ? (entry.bounce_reason || 'Bounced') : undefined}
        />
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{humanise(entry.type_key)}</TableCell>
      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
        {timeAgo(entry.created_at)}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{outcome}</TableCell>
      <TableCell className="text-right" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-center justify-end gap-1">
          {entry.status === 'pending' && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
              disabled={cancellingId === entry.id}
              aria-label={`Cancel email to ${entry.recipient_email}`}
              onClick={() => onCancel(entry.id)}
            >
              {cancellingId === entry.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />}
            </Button>
          )}
          {entry.status === 'failed' && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-amber-600 hover:bg-amber-50 hover:text-amber-700"
              disabled={retryingId === entry.id}
              aria-label={`Retry email to ${entry.recipient_email}`}
              onClick={() => onRetry(entry.id)}
            >
              {retryingId === entry.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
